/**
 * La bozza dello schema (CF04) — CL02.
 *
 * La master modifica una **bozza**, e la bozza non tocca mai ciò da cui è nata:
 * `Annulla` deve ripristinare davvero, e finché non si preme `Salva` lo schema
 * attivo non cambia.
 *
 * Il codice precedente faceva `const cp = [...prev]; cp[idx].name = value`.
 * `[...prev]` copia l'**array**, non le righe: `cp[idx]` è lo stesso oggetto di
 * `prev[idx]` e di `schema[idx]`. Modificare un nome mutava quindi anche la prop
 * del genitore, e annullare non ripristinava niente. Le funzioni qui sotto
 * aggiornano **per id**, mai per posizione, e copiano la riga.
 *
 * Perché per id: la posizione cambia con il riordino, e una modifica in volo su
 * un indice non più valido scriverebbe sulla riga sbagliata.
 */

import type { FieldDefinition } from "model/CustomFields";

/** Copia una definizione, con la sua configurazione annidata. */
export const cloneField = (field: FieldDefinition): FieldDefinition =>
  deepCopy(field);

/**
 * Copia in profondità di un valore JSON.
 *
 * Usa `structuredClone` quando c'è e ripiega sul giro `JSON` quando non c'è:
 * una definizione è JSON puro, quindi i due danno lo stesso risultato, e
 * `structuredClone` non esiste ovunque il codice può girare — non è una
 * preferenza di stile, è una disponibilità.
 */
export const deepCopy = <T>(value: T): T =>
  typeof structuredClone === "function"
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T);

/** Una bozza isolata: modificarla non tocca l'array di partenza. */
export const startDraft = (fields: readonly FieldDefinition[]): FieldDefinition[] =>
  fields.map(cloneField);

/**
 * Aggiorna una riga per `fieldId`.
 *
 * Le righe non toccate restano **le stesse istanze**: un `map` che clonasse
 * tutto a ogni tasto premuto ricostruirebbe l'intera bozza per una lettera.
 */
export function updateField(
  draft: readonly FieldDefinition[],
  fieldId: string,
  changes: Partial<FieldDefinition>
): FieldDefinition[] {
  return draft.map((field) =>
    field.fieldId === fieldId ? { ...cloneField(field), ...changes } : field
  );
}

/** Aggiunge una riga in fondo, senza toccare le altre. */
export function addField(
  draft: readonly FieldDefinition[],
  field: FieldDefinition
): FieldDefinition[] {
  return [...draft, cloneField(field)].map((item, index) => ({
    ...item,
    position: index,
  }));
}

/**
 * Toglie una riga e ricompatta le posizioni.
 *
 * Le posizioni sono indici, non identità: lasciare un buco farebbe credere al
 * server che manchi un campo.
 */
export function removeField(
  draft: readonly FieldDefinition[],
  fieldId: string
): FieldDefinition[] {
  return draft
    .filter((field) => field.fieldId !== fieldId)
    .map((field, index) => (field.position === index ? field : { ...field, position: index }));
}

/**
 * Sposta una riga dove il trascinamento l'ha lasciata.
 *
 * `toIndex` è il punto di rilascio, non uno scambio con il vicino: un gesto
 * solo attraversa più righe, e uno scambio per volta non lo saprebbe
 * rappresentare. Le posizioni seguono l'ordine, come in `removeField`.
 */
export function moveFieldTo(
  draft: readonly FieldDefinition[],
  fieldId: string,
  toIndex: number
): FieldDefinition[] {
  const from = draft.findIndex((field) => field.fieldId === fieldId);
  if (from < 0 || toIndex < 0 || toIndex >= draft.length || toIndex === from) {
    // Una riga che non c'è, o lasciata dov'era: nessuna modifica, ma una copia
    // — il chiamante la mette nello stato, e lo stato non si muta.
    return [...draft];
  }

  const next = [...draft];
  const [moved] = next.splice(from, 1);
  next.splice(toIndex, 0, moved);
  return next.map((field, position) =>
    field.position === position ? field : { ...field, position }
  );
}

/**
 * Se c'è una riga senza nome ma con un tipo scelto.
 *
 * La riga segnaposto in coda nasce di tipo `text` e con label vuota, e una riga
 * senza label non viene salvata: un tipo scelto lì è un intento che andrebbe
 * perso in silenzio. Riconoscere il caso permette di avvisare e bloccare, come
 * per i nomi duplicati.
 */
export function hasUnnamedTypedRow<T extends { label: string; type: string }>(
  rows: readonly T[]
): boolean {
  return rows.some((row) => row.label.trim() === "" && row.type !== "text");
}

/**
 * Se la bozza differisce da ciò da cui è nata.
 *
 * Serve a decidere se avvisare prima di scartare: chiedere conferma su una
 * bozza intatta è rumore, e non chiederla su una modificata perde il lavoro.
 *
 * Generica sull'elemento perché la master convive, durante la transizione, con
 * la riga legacy `{id,name,type,inCard}` e con la definizione del contratto
 * nuovo: il confronto è lo stesso.
 */
export function isDirty<T>(draft: readonly T[], original: readonly T[]): boolean {
  if (draft.length !== original.length) return true;
  return draft.some((item, index) => !sameItem(item, original[index]));
}

/**
 * Se l'unica differenza fra bozza e originale è la preferenza `inCard`.
 *
 * `inCard` dice solo se il campo compare nella card: è una preferenza di
 * presentazione, non cambia la forma dello schema, quindi non c'è nessuna
 * anomalia di forma da annunciare. Bozza intatta → falso (non è una modifica);
 * differenza anche altrove → falso (va analizzata).
 *
 * L'idea è togliere `inCard` da entrambe le liste e rifare lo stesso confronto:
 * due liste che tornano uguali differivano solo per quella proprietà.
 */
export function isInCardOnlyChange<T extends { inCard?: boolean }>(
  draft: readonly T[],
  original: readonly T[]
): boolean {
  if (!isDirty(draft, original)) return false;
  const strip = (items: readonly T[]): Array<Omit<T, "inCard">> =>
    items.map(({ inCard: _inCard, ...rest }) => rest);
  return !isDirty(strip(draft), strip(original));
}

function sameItem(left: unknown, right: unknown): boolean {
  // `JSON.stringify` con chiavi ordinate: due valori sono uguali se lo stesso
  // dato lo è, e un confronto per riferimento direbbe sempre "diverso".
  return stableStringify(left) === stableStringify(right);
}

const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort())
      : item
  );
